/* eslint-disable no-void */

import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FilterService } from 'primeng/api';
import { first, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LayoutService } from './layout/service/layout.service';
import { FormChangeDetectionService } from './services/form-change-detection.service';

import moment from 'moment';
import { CustomerService } from 'src/app/services/api/customer/customer.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { dateFilter } from 'src/app/utils/filter/date.filter';
import { userFilter } from 'src/app/utils/filter/user.filter';

import * as Sentry from '@sentry/angular';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from './services/local-storage.service';
import { ToastModule } from 'primeng/toast';
import { SHARED_IMPORTS } from './utils/other/standalone-import';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { RouterOutlet } from '@angular/router';
import { LoadingComponent } from './components/misc/loading/loading.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    SHARED_IMPORTS,
    ToastModule,
    ConfirmDialogModule,
    RouterOutlet,
    LoadingComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'fast-rocket';

  private formArraySubscription: Subscription = new Subscription();

  private hasUnsavedChanges = false;

  private cssSubscription: Subscription = new Subscription();

  private localStorageSubscription: Subscription = new Subscription();

  // Services start

  private formChangeDetectionService = inject(FormChangeDetectionService);

  private translate = inject(TranslateService);

  private layoutService = inject(LayoutService);

  private authService = inject(AuthService);

  private customerService = inject(CustomerService);

  private filterService = inject(FilterService);

  private http = inject(HttpClient);

  private localStorageService = inject(LocalStorageService);

  constructor() {
    const { defaultLanguage } = environment;
    this.translate.setDefaultLang(defaultLanguage);

    let currentLanguage = localStorage.getItem('language');

    if (!currentLanguage) {
      currentLanguage = defaultLanguage;
      localStorage.setItem('language', defaultLanguage);
    }
    this.translate.use(currentLanguage);

    this.translate.onLangChange.subscribe((event) => {
      moment.locale(event.lang);
    });

    // this.translate
    //   .get('primeng')
    //   .subscribe((res) => this.primengConfig.setTranslation(res));
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const config = JSON.parse(localStorage.getItem('config')!);

    if (config) {
      this.layoutService.layoutConfig.set(config);
    } else {
      this.layoutService.layoutConfig.set({
        ripple: false,
        preset: 'FastRocketPreset',
        inputStyle: 'outlined',
        menuMode: 'static',
        colorScheme: 'light',
        darkTheme: false,
        menuTheme: 'colorScheme'
      });
    }
  }

  ngOnInit(): void {
    // Initialize authentication service
    this.authService
      .init()
      .pipe(first())
      .subscribe((user) => {
        // If user is logged out, do nothing
        if (user === null) {
          return;
        }

        // NOTE: This is a workaround and should be removed in the future (only the timeout)
        setTimeout(() => {
          // Prefetch customers because they have a lot of data
          this.customerService
            .findAll()
            .pipe(first())
            .subscribe(
              (/* they need a subscription to be executed, otherwise*/) => {
                // They get cached in the service
              }
            );

          // Timeout to prevent blocking other requests
        }, 4000);
      });

    // Subscribe to form array changes to detect unsaved changes
    this.formArraySubscription = this.formChangeDetectionService
      .getFormArrayChanges()
      .subscribe((changes: FormGroup[]) => {
        if (changes && changes.length > 0) {
          this.hasUnsavedChanges = true;
        } else {
          this.hasUnsavedChanges = false;
        }
      });

    // Register filters with the filter service
    this.filterService.register('user', userFilter);
    this.filterService.register('date', dateFilter);

    // this.initSentry();
  }

  ngOnDestroy() {
    this.formArraySubscription.unsubscribe();
  }

  /**
   * Listens for the window close event and prevents it if there are unsaved changes.
   * @param {Event} event - The window close event.
   */
  @HostListener('window:close', ['$event'])
  handleWindowClose(event: Event) {
    // Check if there are unsaved changes
    if (this.hasUnsavedChanges) {
      // Prevent the default action (closing the window)
      event.preventDefault();
    }
  }

  /**
   * Listens for the window beforeunload event and prevents it if there are unsaved changes
   * and the environment is set to production.
   * @param {Event} event - The window beforeunload event.
   */
  @HostListener('window:beforeunload', ['$event'])
  handleWindowReload(event: Event) {
    // Allow reload if the environment is not production
    if (environment.production === false) {
      return;
    }

    // Check if there are unsaved changes
    if (this.hasUnsavedChanges) {
      // Prevent the default action (reloading the window)
      event.preventDefault();
    }
  }

  /**
   * Initializes Sentry for error tracking and feedback collection.
   * This method configures Sentry with various settings and translations for the feedback form.
   *
   * @returns {Promise<void>} A promise that resolves when Sentry is initialized.
   */
  private async initSentry(): Promise<void> {
    // Determine if Sentry should be enabled based on the environment
    const SENTRY_ENABLED =
      environment.env === 'production' || environment.env === 'staging';
    // Fetch translations for the feedback form
    const triggerLabel = await this.getTranslationByKey(
      'feedback.triggerLabel'
    );
    const formTitle = await this.getTranslationByKey('feedback.formTitle');
    const nameLabel = await this.getTranslationByKey('feedback.nameLabel');
    const namePlaceholder = await this.getTranslationByKey(
      'feedback.namePlaceholder'
    );
    const emailLabel = await this.getTranslationByKey('feedback.emailLabel');
    const emailPlaceholder = await this.getTranslationByKey(
      'feedback.emailPlaceholder'
    );
    const messageLabel = await this.getTranslationByKey(
      'feedback.messageLabel'
    );
    const messagePlaceholder = await this.getTranslationByKey(
      'feedback.messagePlaceholder'
    );
    const isRequiredLabel = await this.getTranslationByKey(
      'feedback.isRequiredLabel'
    );
    const addScreenshotButtonLabel = await this.getTranslationByKey(
      'feedback.addScreenshotButtonLabel'
    );
    const removeScreenshotButtonLabel = await this.getTranslationByKey(
      'feedback.removeScreenshotButtonLabel'
    );
    const submitButtonLabel = await this.getTranslationByKey(
      'feedback.submitButtonLabel'
    );
    const cancelButtonLabel = await this.getTranslationByKey(
      'feedback.cancelButtonLabel'
    );
    const confirmButtonLabel = await this.getTranslationByKey(
      'feedback.confirmButtonLabel'
    );
    const errorGeneric = await this.getTranslationByKey(
      'feedback.errorGeneric'
    );
    const errorFormEntry = await this.getTranslationByKey(
      'feedback.errorFormEntry'
    );
    const successMessage = await this.getTranslationByKey(
      'feedback.successMessage'
    );
    // Initialize Sentry with the fetched translations and other settings
    Sentry.init({
      dsn: environment.sentryDsn,
      beforeSend(event) {
        // Check if it is an exception, and if so, show the report dialog
        if (event.exception && event.event_id) {
          Sentry.showReportDialog({ eventId: event.event_id });
        }

        return event;
      },
      integrations: [
        Sentry.browserTracingIntegration(),
        // Sentry.replayIntegration(),
        Sentry.feedbackIntegration({
          // Additional SDK configuration goes in here, for example:
          colorScheme: 'system',
          triggerLabel,
          formTitle,
          showBranding: false,
          nameLabel,
          namePlaceholder,
          emailLabel,
          emailPlaceholder,
          messageLabel,
          messagePlaceholder,
          isRequiredLabel,
          addScreenshotButtonLabel,
          removeScreenshotButtonLabel,
          submitButtonLabel,
          cancelButtonLabel,
          confirmButtonLabel,
          errorGeneric,
          errorFormEntry,
          successMessage
        })
      ],
      // Tracing
      tracesSampleRate: 1.0, // Capture 100% of the transactions
      environment: environment.env,
      enabled: SENTRY_ENABLED
    });
    this.generateCustomSentryStyle();
  }

  /**
   * Retrieves the translation for a given key.
   * This method uses the translation service to fetch the translation for the specified key.
   *
   * @param {string} key - The key for which the translation should be fetched.
   * @returns {Promise<string>} A promise that resolves to the translation string.
   */
  private getTranslationByKey(key: string): Promise<string> {
    // Use the translation service to fetch the translation for the specified key
    return this.translate.get(key).toPromise();
  }

  /**
   * Generates custom Sentry styles based on the user's color scheme preference.
   * This method retrieves the user's color scheme from local storage and applies the corresponding styles to the Sentry feedback widget.
   * @returns {void}
   */
  private generateCustomSentryStyle(): void {
    // Select the Sentry feedback host element
    const host = document.querySelector('#sentry-feedback');
    let color = 'light';
    let generated = false;
    if (host) {
      const { shadowRoot } = host;
      if (shadowRoot) {
        // Subscribe to local storage changes to get the user's color scheme preference
        this.localStorageSubscription =
          this.localStorageService.itemValue.subscribe((value: string) => {
            if (value) {
              const { colorScheme } = JSON.parse(value);
              if (colorScheme) {
                color = colorScheme;
                this.generateCustomSentryCss(color, shadowRoot);
                generated = true;
              }
            }
          });
        // If no color scheme is found, generate default styles
        if (!generated) {
          this.generateCustomSentryCss(color, shadowRoot);
        }
      }
    }
  }

  /**
   * Generates custom Sentry CSS based on the specified color scheme.
   * This method loads the corresponding CSS file and injects it into the Shadow DOM of the Sentry feedback widget.
   *
   * @param {string} color - The color scheme to be applied (e.g., 'light' or 'dark').
   * @param {ShadowRoot} shadowRoot - The Shadow DOM of the Sentry feedback widget.
   *
   * @returns {void}
   */
  private generateCustomSentryCss(color: string, shadowRoot: ShadowRoot): void {
    // Load the CSS file corresponding to the specified color scheme
    this.cssSubscription = this.http
      .get(`assets/sentry/sentry_${color}.scss`, { responseType: 'text' })
      .pipe(first())
      .subscribe((css: string) => {
        // Create a style element and inject the loaded CSS into the Shadow DOM
        const style = document.createElement('style');
        style.textContent = css;
        shadowRoot.appendChild(style);
      });
  }
}
